import { Injectable } from '@angular/core';

import { Observable, distinctUntilChanged, shareReplay, startWith, switchMap } from 'rxjs';

import * as LDClient from 'launchdarkly-js-client-sdk';

import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

/**
 * Service providing observable LaunchDarkly clients.
 */
@Injectable()
export class LaunchDarklyService {

    /**
	 * Constructor
	 * @param authService To get user data.
	 */
	constructor(private authService: AuthService) {
        
    }

	private client = LDClient.initialize(environment.launchDarklyConfig.clientId, {
		kind: 'user',
		key: 'anon',
		anonymous: true,
	});

	/**
	 * Observable of the LaunchDarkly client. Initialized once; context is updated via identify() when user data changes.
	 */
	private launchDarklyClient$ = this.authService.userData$.pipe(
		switchMap(userData => {
			const context = {
				kind: 'user',
				key: userData?.userName ? userData.userName : 'anon',
				company: userData?.companyName ? userData.companyName : 'N/A',
				anonymous: !userData?.sessionID,
			};
			return new Observable<LDClient.LDClient>(subscriber => {
				this.client.identify(context).then(() => {
					subscriber.next(this.client);
					subscriber.complete();
				}).catch(err => {
					subscriber.next(this.client);
					subscriber.complete();
				});
			});
		}),
		startWith(this.client),
		shareReplay(1),
	);

	/**
	 * Observable of the LaunchDarkly flags, using the client observable above.
	 * Emits an empty flags object immediately so dependents don't block on LD initialization.
	 */
	public launchDarklyFlags$ = this.launchDarklyClient$.pipe(
		switchMap(
			client =>
				new Observable<{ [key: string]: any }>(subscriber => {
					subscriber.next({});

					const onReady = () => {
						try {
							subscriber.next(client.allFlags());
						} catch (error) {
							console.error('Error in ready handler:', error);
						}
					};

					const onChange = () => {
						try {
							subscriber.next(client.allFlags());
						} catch (error) {
							console.error('Error in change handler:', error);
						}
					};

					client.on('ready', onReady);
					client.on('change', onChange);

					return () => {
						client.off('ready', onReady);
						client.off('change', onChange);
					};
				}),
		),
		distinctUntilChanged(),
		shareReplay(1),
	);
}
